import { trigger } from "redial";
import { history } from "../../../client/history";
import {
  setPageName,
  setPageLoading,
  setIsServerRendered,
} from "../../../state/modules/page/actions";
import clientSideRedirect from "../../../client/clientSideRedirect";
import { PAGE_NOT_FOUND } from "../../../state/modules/page/constants";
import getIsNewPage from "./getIsNewPage";
import getIsPageNumberChanged from "./getIsPageNumberChanged";
import matchRoutes from "../../../routes/matchRoutes";
import { clearCategoryError } from "../../../state/modules/category";
import getDataFetchingParameters from "./getDataFetchingParameters";
import { getCanonicalPath } from "../../../state/modules/router/selectors";
import { setupExperiments } from "../setupExperiments";
import { getFloorFiltersForNewSearch } from "../experimentFloorFilters";
import {
  getRestoredFiltersForNavigation,
  buildLocationWithRestoredFilters,
  saveCurrentFiltersFromLocation,
} from "../../../client/experimentSaveFilters";
import { setNewSearch } from "../../../state/modules/search";
import { getSuccessMessage } from "../../../state/modules/layout/selectors";
import {
  hideSuccessMessage,
  setDetectedFloor,
} from "../../../state/modules/layout/actions";
import { getIsLoadingMore } from "../../../state/modules/search/domains/pagination/selectors/getIsLoadingMore";
import { selectSearchIsPagingBackward } from "../../../state/modules/search/domains/ui/selectors";

let shortCircuit = false;
let lastKnownGoodLocation;

const detectFloorFromUrl = (pathname) => {
  if (/^\/men(\/|$)/i.test(pathname)) return "men";
  if (/^\/women(\/|$)/i.test(pathname)) return "women";

  return null;
};

const shouldCheckPageNumberChange = ({
  navigationAction,
  isPaginatingInline,
}) => navigationAction !== "REPLACE" && !isPaginatingInline;

const setupRouteChangeListener = (store, decisions) => {
  lastKnownGoodLocation = history.location;
  const initialFloor = detectFloorFromUrl(history.location.pathname);
  if (initialFloor) {
    store.dispatch(setDetectedFloor(initialFloor));
  }
  setupExperiments(store, decisions, history.location);
  history.listen((location, navigationAction) => {
    if (shortCircuit || location.isPrefilterGenderedSearch) {
      return;
    }

    const { pathname, search: query } = location;

    const { dispatch } = store;
    const detectedFloor = detectFloorFromUrl(pathname);
    if (detectedFloor) {
      dispatch(setDetectedFloor(detectedFloor));
    }

    const activeRoute = matchRoutes({ pathname });

    if (activeRoute.key === PAGE_NOT_FOUND) {
      clientSideRedirect({ response: { redirectUrl: `${pathname}${query}` } });
    } else if (activeRoute.key) {
      const { dispatch, getState } = store;
      const state = getState();
      const { category, search, page } = state;
      const catId = category.details?.id;
      const searchTerm = search?.searchTerm;
      const currentPageNumber = parseInt(search?.query?.page, 10) || 1;
      const isPaginatingInline =
        getIsLoadingMore(state) || selectSearchIsPagingBackward(state);
      const { isVisible: isSuccessMessageVisible } = getSuccessMessage(state);

      const isNewPage = getIsNewPage({
        query,
        catId,
        searchTerm,
        page,
      });
      const isPageNumberChanged =
        shouldCheckPageNumberChange({
          navigationAction,
          isPaginatingInline,
        }) &&
        getIsPageNumberChanged({
          query,
          currentPageNumber,
        });
      const shouldFetchNewResults = isNewPage || isPageNumberChanged;

      activeRoute.props.component.load().then((loadedComponent) => {
        const currentLocation = history.location;
        if (
          currentLocation.pathname !== location.pathname ||
          currentLocation.search !== location.search
        ) {
          return;
        }

        let effectiveLocation = location;

        const floorFilters = getFloorFiltersForNewSearch(
          store,
          effectiveLocation,
        );
        if (floorFilters) {
          const searchParams = new URLSearchParams(effectiveLocation.search);
          searchParams.append("refine", floorFilters.refine);
          const modifiedLocation = {
            ...effectiveLocation,
            search: `?${searchParams.toString()}`,
            isPrefilterGenderedSearch: true,
          };
          history.replace(modifiedLocation);
          effectiveLocation = modifiedLocation;
        }

        const restoredFilters =
          getRestoredFiltersForNavigation(effectiveLocation);
        if (restoredFilters) {
          effectiveLocation = buildLocationWithRestoredFilters(
            effectiveLocation,
            restoredFilters,
          );
          dispatch(setNewSearch());
          shortCircuit = true;
          history.replace(effectiveLocation);
          shortCircuit = false;
        }

        const dataFetchingParameters = getDataFetchingParameters({
          store,
          location: effectiveLocation,
          dispatch,
          isNewPage,
          lastKnownGoodLocation,
          category,
          decisions,
        });

        if (shouldFetchNewResults) {
          dispatch(setNewSearch());
          dispatch(setPageLoading(true));
        }

        if (isNewPage) {
          dispatch(clearCategoryError());
          dispatch(setPageName(activeRoute.key));
          setupExperiments(store, decisions, effectiveLocation);

          if (isSuccessMessageVisible) {
            dispatch(hideSuccessMessage());
          }
        }

        dispatch(setIsServerRendered(false));

        const { pathname: lastKnownGoodPathname } = lastKnownGoodLocation;

        trigger("fetch", loadedComponent.default, dataFetchingParameters).then(
          () => {
            const state = getState();
            const { category, error } = state;
            if (error) {
              shortCircuit = true;
              effectiveLocation = lastKnownGoodLocation;
              history.replace(lastKnownGoodLocation);
            }

            if (!error && category.details?.id) {
              const canonicalPath = getCanonicalPath(state);

              if (lastKnownGoodPathname !== canonicalPath) {
                shortCircuit = true;
                effectiveLocation = {
                  ...effectiveLocation,
                  pathname: canonicalPath,
                };
                history.replace(effectiveLocation);
              }
            }
            shortCircuit = false;
            lastKnownGoodLocation = effectiveLocation;
            saveCurrentFiltersFromLocation(effectiveLocation);
          },
        );
      });
    }
  });

  const initialRestoredFilters = getRestoredFiltersForNavigation(
    history.location,
  );
  if (initialRestoredFilters) {
    const restoredLocation = buildLocationWithRestoredFilters(
      history.location,
      initialRestoredFilters,
    );
    store.dispatch(setNewSearch());
    history.replace(restoredLocation);
  }
};

export default setupRouteChangeListener;
